import { useState, useEffect } from "react";
import { useTheme, getAuthColors } from "./ThemeContext";

const DISPLAY = "'Barlow Condensed', sans-serif";
const UI      = "'Barlow', sans-serif";
const MONO    = "'Roboto Mono', monospace";

const DEFAULT_EMAIL    = "admin@plunger.com";
const DEFAULT_PASSWORD = "admin123";

const SIGNIN_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800&family=Barlow:wght@300;400;500;600;700&family=Roboto+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }

  @keyframes float-p {
    0%   { transform: translateY(0) translateX(0); opacity: 0; }
    10%  { opacity: 0.5; } 90% { opacity: 0.5; }
    100% { transform: translateY(-110px) translateX(24px); opacity: 0; }
  }
  @keyframes scan { 0% { top: -1px; } 100% { top: 100%; } }
  @keyframes fade-up {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse-glow-dark {
    0%, 100% { box-shadow: 0 0 0 1px #111d2e, 0 24px 64px #00000066; }
    50%       { box-shadow: 0 0 0 1px #22d3ee18, 0 24px 64px #22d3ee0a; }
  }
  @keyframes pulse-glow-light {
    0%, 100% { box-shadow: 0 0 0 1px #d0daea, 0 24px 64px #0000001a; }
    50%       { box-shadow: 0 0 0 1px #006ea844, 0 24px 64px #006ea808; }
  }
  @keyframes logo-pulse {
    0%, 100% { filter: drop-shadow(0 0 6px #22d3ee44); }
    50%       { filter: drop-shadow(0 0 18px #22d3ee99); }
  }
  @keyframes ring-cw  { to { transform: rotate(360deg);  } }
  @keyframes ring-ccw { to { transform: rotate(-360deg); } }

  .si-input {
    border-radius: 6px; font-family: 'Barlow', sans-serif; font-size: 14px;
    font-weight: 400; line-height: 1.5; padding: 11px 14px; width: 100%;
    outline: none; transition: border-color 0.18s, box-shadow 0.18s, background 0.25s, color 0.25s;
    letter-spacing: 0.2px;
  }
  .si-btn-primary {
    width: 100%; padding: 13px 20px; border-radius: 6px; border: none; cursor: pointer;
    font-family: 'Barlow', sans-serif; font-weight: 700; font-size: 15px; letter-spacing: 1px;
    text-transform: uppercase; transition: filter 0.18s, transform 0.12s, box-shadow 0.18s;
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #a78bfa 0%, #22d3ee 100%);
    color: #fff; box-shadow: 0 4px 20px #a78bfa22;
  }
  .si-btn-primary:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 8px 28px #a78bfa33; }
  .si-btn-primary:active:not(:disabled) { transform: translateY(0); }
  .si-btn-primary::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 55%);
    pointer-events: none;
  }
  .si-link {
    cursor: pointer; font-family: 'Barlow', sans-serif; font-size: 14px;
    font-weight: 600; text-decoration: none; transition: color 0.18s;
    background: none; border: none; padding: 0; letter-spacing: 0.3px;
  }
  .si-link:hover { text-decoration: underline; }
  .si-label {
    font-family: 'Barlow', sans-serif; font-size: 11px; font-weight: 700;
    display: block; margin-bottom: 7px; letter-spacing: 1.5px; text-transform: uppercase;
  }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { border-radius: 2px; }
`;

function injectCSS() {
  if (document.getElementById("si-css")) return;
  const s = document.createElement("style");
  s.id = "si-css";
  s.textContent = SIGNIN_CSS;
  document.head.appendChild(s);
}

function initStore() {
  if (!window.__plunger_users) window.__plunger_users = [];
}

async function loadUsers() {
  initStore();
  if (window.__plunger_users.length > 0) return window.__plunger_users;
  try {
    const res = await window.storage.get("plunger_users");
    if (res && res.value) window.__plunger_users = JSON.parse(res.value);
  } catch {}
  return window.__plunger_users;
}

function Particles({ C }) {
  const pts = Array.from({ length: 12 }, (_, i) => ({
    id: i, left: `${6 + (i * 7.5) % 88}%`, top: `${25 + (i * 10) % 58}%`,
    delay: `${(i * 0.7) % 9}s`, dur: `${8 + (i * 0.4) % 5}s`,
    size: i % 3 === 0 ? 2 : 1.5, color: i % 2 === 0 ? C.cyan : C.purple,
  }));
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
      {pts.map(p => (
        <div key={p.id} style={{
          position:"absolute", left:p.left, top:p.top,
          width:p.size, height:p.size, borderRadius:"50%",
          background:p.color, boxShadow:`0 0 4px ${p.color}66`,
          animation:`float-p ${p.dur} ${p.delay} infinite ease-in-out`,
        }}/>
      ))}
    </div>
  );
}

function LogoRing({ C }) {
  return (
    <div style={{ position:"relative", width:68, height:68, margin:"0 auto 20px" }}>
      <svg style={{ position:"absolute", inset:0, animation:"ring-cw 10s linear infinite" }} width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r="31" fill="none" stroke={C.border2} strokeWidth="1"/>
        <circle cx="34" cy="34" r="31" fill="none" stroke={C.cyan} strokeWidth="1.5" strokeDasharray="16 9" strokeLinecap="square" opacity="0.55"/>
      </svg>
      <svg style={{ position:"absolute", inset:9, animation:"ring-ccw 6s linear infinite" }} width="50" height="50" viewBox="0 0 50 50">
        <circle cx="25" cy="25" r="22" fill="none" stroke={C.purple} strokeWidth="1" strokeDasharray="7 11" strokeLinecap="square" opacity="0.4"/>
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", animation:"logo-pulse 3s ease-in-out infinite" }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ filter:"drop-shadow(0 0 6px #22d3ee66)", userSelect:"none" }}>
          <line x1="12" y1="2" x2="5" y2="10"/>
          <line x1="12" y1="2" x2="19" y2="10"/>
          <line x1="5" y1="10" x2="19" y2="10"/>
          <line x1="7" y1="10" x2="5" y2="22"/>
          <line x1="17" y1="10" x2="19" y2="22"/>
          <line x1="5" y1="22" x2="19" y2="22"/>
          <line x1="12" y1="2" x2="12" y2="22"/>
          <line x1="8" y1="14" x2="16" y2="14"/>
          <line x1="7" y1="18" x2="17" y2="18"/>
          <rect x="10" y="19" width="4" height="3" rx="0.5"/>
        </svg>
      </div>
    </div>
  );
}
function Alert({ type, children, C }) {
  const map = { error:[C.red,"!"], success:[C.green,"✓"] };
  const [color, icon] = map[type];
  return (
    <div style={{
      fontFamily:UI, fontSize:13, fontWeight:400, color,
      background:`${color}0c`, border:`1px solid ${color}25`,
      borderRadius:5, padding:"10px 14px",
      display:"flex", alignItems:"flex-start", gap:10, lineHeight:1.5,
    }}>
      <span style={{ fontFamily:MONO, fontSize:12, marginTop:1, flexShrink:0, fontWeight:700 }}>[{icon}]</span>
      <span>{children}</span>
    </div>
  );
}

/* Theme Toggle Button */
function ThemeToggle({ isDark, toggle }) {
  return (
    <button
      onClick={toggle}
      title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
      style={{
        position: "absolute", top: 14, right: 14,
        width: 36, height: 20, borderRadius: 10,
        background: isDark ? "#162640" : "#d0daea",
        border: `1px solid ${isDark ? "#22d3ee33" : "#b8c8e0"}`,
        cursor: "pointer", padding: 0, display: "flex", alignItems: "center",
        transition: "background 0.25s, border-color 0.25s",
      }}
    >
      <span style={{
        width: 14, height: 14, borderRadius: "50%",
        background: isDark ? "#22d3ee" : "#006ea8",
        marginLeft: isDark ? 2 : 18,
        transition: "margin-left 0.25s, background 0.25s",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8,
      }}>
        {isDark ? "🌙" : "☀️"}
      </span>
    </button>
  );
}

function DefaultAccessCard({ onFill, C }) {
  return (
    <button
      onClick={onFill}
      type="button"
      style={{
        width: "100%", border: `1px solid ${C.border2}`, borderRadius: 8,
        background: "transparent", cursor: "pointer", padding: "14px 16px",
        textAlign: "left", transition: "border-color 0.18s, background 0.18s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.cyan + "55"; e.currentTarget.style.background = C.cyan + "08"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border2; e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:C.green, boxShadow:`0 0 6px ${C.green}` }}/>
          <span style={{ fontFamily:MONO, fontSize:10, color:C.teal, letterSpacing:"1.5px", textTransform:"uppercase", fontWeight:700 }}>
            Default Access
          </span>
        </div>
        <span style={{ fontFamily:UI, fontSize:11, color:C.muted }}>tap to auto-fill</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontFamily:MONO, fontSize:10, color:C.muted, letterSpacing:"1px", textTransform:"uppercase", width:32, flexShrink:0 }}>email</span>
          <span style={{ fontFamily:MONO, fontSize:13, fontWeight:500, color:C.cyan }}>{DEFAULT_EMAIL}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontFamily:MONO, fontSize:10, color:C.muted, letterSpacing:"1px", textTransform:"uppercase", width:32, flexShrink:0 }}>pass</span>
          <span style={{ fontFamily:MONO, fontSize:13, fontWeight:500, color:C.purple }}>{DEFAULT_PASSWORD}</span>
        </div>
      </div>
    </button>
  );
}

export default function SignIn({ onSignIn, onGoSignUp }) {
  const { isDark, toggle } = useTheme();
  const C = getAuthColors(isDark);

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => { injectCSS(); }, []);

  function fillDefaults() {
    setEmail(DEFAULT_EMAIL);
    setPassword(DEFAULT_PASSWORD);
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    await new Promise(r => setTimeout(r, 700));

    const trimmedEmail = email.trim();

    if (trimmedEmail === DEFAULT_EMAIL && password === DEFAULT_PASSWORD) {
      const user = { email: DEFAULT_EMAIL, name: "Admin" };
      try { localStorage.setItem("plunger_user", JSON.stringify(user)); } catch {}
      onSignIn(user);
      setLoading(false);
      return;
    }

    const users = await loadUsers();
    const match = users.find(u => u.email === trimmedEmail && u.password === password);

    if (match) {
      const user = { email: match.email, name: match.name };
      try { localStorage.setItem("plunger_user", JSON.stringify(user)); } catch {}
      onSignIn(user);
    } else {
      setError("Invalid email or password.");
    }
    setLoading(false);
  }

  const inputStyle = {
    background:  C.inputBg,
    border:      `1px solid ${C.inputBorder}`,
    color:       C.inputText,
  };

  return (
    <div style={{
      minHeight:"100vh", background:C.bg,
      display:"flex", alignItems:"center", justifyContent:"center",
      position:"relative", fontFamily:UI, overflow:"hidden",
      transition: "background 0.25s",
    }}>
      {/* Grid background */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none",
        backgroundImage:`linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
        backgroundSize:"48px 48px", opacity: isDark ? 0.25 : 0.4,
      }}/>
      {/* Glow orb */}
      <div style={{
        position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        width:600, height:600, borderRadius:"50%",
        background:`radial-gradient(circle, ${C.purple}08 0%, ${C.teal}05 50%, transparent 70%)`,
        pointerEvents:"none",
      }}/>
      <Particles C={C}/>

      {/* Card */}
      <div style={{
        position:"relative", width:415, maxWidth:"calc(100vw - 28px)",
        background:C.card, border:`1px solid ${C.border2}`, borderRadius:10,
        padding:"40px 36px 32px",
        animation:`fade-up 0.45s cubic-bezier(0.22,1,0.36,1) both, ${isDark ? "pulse-glow-dark" : "pulse-glow-light"} 5s ease-in-out 0.5s infinite`,
        zIndex:1, transition: "background 0.25s, border-color 0.25s",
        boxShadow: isDark ? "0 24px 64px #00000066" : "0 24px 64px #0000001a",
      }}>
        {/* Scan line */}
        <div style={{
          position:"absolute", left:0, right:0, height:"1px",
          background:`linear-gradient(90deg, transparent, ${C.cyan}44, transparent)`,
          animation:"scan 5s linear infinite", pointerEvents:"none",
        }}/>

        {/* Theme toggle */}
        <ThemeToggle isDark={isDark} toggle={toggle} />

        <LogoRing C={C}/>

        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{
            fontFamily:DISPLAY, fontWeight:800, fontSize:32, letterSpacing:"2px",
            lineHeight:1.2, textTransform:"uppercase",
            backgroundImage:`linear-gradient(135deg, ${C.cyan} 0%, ${C.purple} 100%)`,
            WebkitBackgroundClip:"text",
            backgroundClip:"text",
            WebkitTextFillColor:"transparent",
            MozBackgroundClip:"text",
            MozTextFillColor:"transparent",
            display:"inline-block",
            transition: "background-image 0.25s",
          }}>
            Plunger Lift AI
          </div>
          <div style={{ fontFamily:UI, fontSize:13, fontWeight:400, color:C.sub, marginTop:9, letterSpacing:"0.3px" }}>
            Your AI-Powered Plunger Lift Platform
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div>
            <label className="si-label" style={{ color: C.muted }}>Email Address</label>
            <input className="si-input" style={inputStyle} type="email" placeholder="you@company.com"
              value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email"/>
          </div>
          <div>
            <label className="si-label" style={{ color: C.muted }}>Password</label>
            <div style={{ position:"relative" }}>
              <input className="si-input" style={{ ...inputStyle, paddingRight:42 }}
                type={showPass ? "text" : "password"}
                placeholder="Enter your password" value={password}
                onChange={e => setPassword(e.target.value)} required/>
              <button type="button" onClick={() => setShowPass(v => !v)} style={{
                position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
                background:"none", border:"none", cursor:"pointer",
                color:C.muted, fontSize:13, padding:2, lineHeight:1,
              }}>
                {showPass ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <Alert type="error" C={C}>{error}</Alert>}

          <button type="submit" className="si-btn-primary" disabled={loading} style={{ marginTop:4 }}>
            {loading ? "Verifying..." : "Sign In →"}
          </button>
        </form>

        <div style={{ marginTop:16 }}>
          <DefaultAccessCard onFill={fillDefaults} C={C}/>
        </div>

        <div style={{ textAlign:"center", marginTop:20, fontFamily:UI, fontSize:13.5, fontWeight:400, color:C.sub }}>
          No account?{" "}
          <button className="si-link" style={{ color: C.cyan }} onClick={onGoSignUp}>Create one</button>
        </div>
        <div style={{ textAlign:"center", marginTop:14, fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:"2px", textTransform:"uppercase", opacity:0.6 }}>
          Secured · Plunger Lift AI
        </div>
      </div>
    </div>
  );
}